import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient from '../config/axios';
import { Loader2, RefreshCcw, AlertTriangle, CheckCircle2, Clock3 } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'read', label: 'Read' },
  { value: 'failed', label: 'Failed' }
];

const badgeClass = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'read':
      return 'bg-emerald-100 text-emerald-800';
    case 'delivered':
      return 'bg-blue-100 text-blue-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-amber-100 text-amber-800';
  }
};

const formatDateTime = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  return x.toLocaleString();
};

const WhatsAppLogs = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.get('/api/whatsapp-analytics/message-logs', {
        params: { page, limit, status: status || undefined, search: search || undefined }
      });
      if (data?.success) {
        setLogs(data.logs || []);
        setTotal(Number(data.total || 0));
      } else {
        setLogs([]);
        setTotal(0);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load WhatsApp logs');
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, search]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const onApplyFilters = () => {
    setPage(1);
    loadLogs();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">WhatsApp Logs</h1>
        <p className="text-gray-600">Workspace-level delivery and failure logs with webhook reasons.</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Phone, contact name, message text"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={onApplyFilters}
              className="w-full rounded-lg bg-blue-600 text-white px-3 py-2 text-sm font-medium hover:bg-blue-700"
            >
              Apply
            </button>
            <button
              onClick={loadLogs}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
              title="Refresh logs"
            >
              <RefreshCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-14">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="p-6 text-red-700 bg-red-50 border border-red-200 m-4 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <span>{error}</span>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No logs found for this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">To / Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failure Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {formatDateTime(row.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="font-medium">{row.to_number || row.contact_phone || '—'}</div>
                      <div className="text-xs text-gray-500">{row.contact_name || 'Unknown contact'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-[340px] truncate" title={row.message_text}>
                      {row.message_text || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${badgeClass(row.status)}`}>
                        {row.status === 'read' ? <CheckCircle2 className="w-3 h-3" /> : <Clock3 className="w-3 h-3" />}
                        {String(row.status || 'sent')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.status === 'failed' ? (
                        <>
                          <div className="font-medium text-red-700">{row.error_code || 'FAILED'}</div>
                          <div className="text-xs text-red-600">{row.error_message || 'No details from WhatsApp'}</div>
                        </>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {(page - 1) * limit + 1}-{Math.min(page * limit, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded border border-gray-300 text-sm disabled:opacity-50 hover:bg-white"
            >
              Prev
            </button>
            <span className="text-sm text-gray-700">Page {page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 rounded border border-gray-300 text-sm disabled:opacity-50 hover:bg-white"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppLogs;
